import AddIcon from '@mui/icons-material/Add';
import { Box, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import {
  ArrayInput,
  BooleanInput,
  Button,
  DateField,
  DateInput,
  DeleteButton,
  Edit,
  FormDataConsumer,
  FormTab,
  Identifier,
  ReferenceArrayInput,
  ReferenceField,
  SaveButton,
  SelectArrayInput,
  SelectInput,
  ShowButton,
  SimpleFormIterator,
  TabbedForm,
  TextField,
  TextInput,
  Toolbar,
  TopToolbar,
  email,
  minLength,
  required,
  useDataProvider,
  usePermissions,
  useRecordContext,
  useSimpleFormIterator
} from 'react-admin';
import { BlockCustomerToggle } from '../../components/BlockCustomerToggle';
import { AddCoupon, BalanceButton, CheckChainUsesModule, CustomerUnifiedSendButton, ModuleKey } from '../../components';
import Permission from '../../components/Permission';
import { PERMISSIONS } from '../../constants';
import { TagType } from '../../models';
import { dateInputFormatter, onlyNumbers, validateFurtherDate } from '../../utils';

const validatePassword = [minLength(6)]

// 10 ou 11 dígitos, igual ao Joi do PATCH /customers (min 10, max 11): ~1.300 clientes têm
// celular antigo de 10 dígitos e com minLength(11) não dava nem para corrigir o nome deles.
const validateCellphone = [minLength(10)]
const validateCellphoneRequired = [required(), minLength(10)]

// Quem já tem celular não pode ficar sem: apagar e salvar mandava cellphone '' no PATCH e
// o backend recusa (400). Cliente que nunca teve (o cadastro pelo app não exige) segue
// editável sem celular, senão o admin não deixaria nem corrigir o nome dele.
const CellphoneInput = () => {
  const record = useRecordContext();

  return (
    <TextInput
      InputProps={{ autoComplete: 'off' }}
      source="cellphone"
      label="Telefone"
      validate={record?.cellphone ? validateCellphoneRequired : validateCellphone}
      sx={{ minWidth: 300 }}
      format={(value) => {
        if (!value || value === "") {
          return "";
        }
        const ddd = value.substr(0, 2)
        const firstStep = value.substr(2, 5)
        const secondStep = value.substr(7, 4)
        let phoneString = `(${ddd}`;

        if (firstStep) {
          phoneString += `) ${firstStep}`;
        }
        if (secondStep) {
          phoneString += `-${secondStep}`;
        }
        return phoneString;
      }}
      parse={(value) => {
        if (!value || value === '') {
          return '';
        }
        return onlyNumbers(value).substring(0, 11);
      }}
    />
  );
};

// Observação salva não se edita nem se apaga: vira texto com autor e data.
const SavedNote = ({ note }: { note: any }) => (
  <Box sx={{ py: 1, maxWidth: 640 }}>
    <Typography sx={{ whiteSpace: 'pre-wrap', fontSize: 15 }}>{note.note}</Typography>
    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 0.75, mt: 0.5, color: 'text.secondary', '& .MuiTypography-root': { fontSize: 13, color: 'inherit' } }}>
      <ReferenceField record={note} source="employeeId" reference="employees" link={false} emptyText="Não identificado">
        <TextField source="name" />
      </ReferenceField>
      <span>·</span>
      <DateField record={note} source="createdAt" showTime locales="pt-BR" options={{ dateStyle: 'short', timeStyle: 'short' }} />
    </Box>
  </Box>
);

const AddNoteButton = () => {
  const { add } = useSimpleFormIterator();
  return <Button label="Adicionar observação" startIcon={<AddIcon />} onClick={() => add()} />;
};

const EditActions = () => (
  <TopToolbar>
    <ShowButton />
  </TopToolbar>
);

const CustomToolbar = () => {
  const record = useRecordContext();

  return (
    <Toolbar sx={{ justifyContent: 'space-between' }}>
      <SaveButton />
      <div
        style={{ display: 'flex', alignItems: 'center', width: '300px', justifyContent: 'flex-end' }}
      >
        <Permission permission={PERMISSIONS.BLOCK_CUSTOMER}>
          <BlockCustomerToggle customer={record} showIcon sx={{ marginRight: 2 }} />
        </Permission>
        <DeleteButton />
      </div>
    </Toolbar>
  )
}

export default props => {
  const [displayBalanceModal, setDisplayBalanceModal] = useState(false);
  const [displayCouponModal, setDisplayCouponModal] = useState(false);
  const [displayNotificationModal, setDisplayNotificationModal] = useState(false);
  const dataProvider = useDataProvider();
  const [customerGroups, setCustomerGroups] = useState<any[]>([]);
  const { permissions } = usePermissions();

  useEffect(() => {
    dataProvider.getList('chains/chainId/customer-groups', {
      pagination: { page: 1, perPage: 1000 },
      sort: { field: 'name', order: 'ASC' },
      filter: {}
    }).then((response) => {
      const groups: Array<{ id: Identifier | null; name: string }> = [{ id: null, name: 'Sem grupo' }];
      response && response.data && response.data.forEach(group => {
        groups.push({
          id: group.id,
          name: group.name
        });
      });
      setCustomerGroups(groups);
    });
  }, []);

  return (
    <Permission permission={PERMISSIONS.UPDATE_CUSTOMER}>
      <Edit {...props} title="Editar cliente" actions={<EditActions />}>
        <TabbedForm toolbar={<CustomToolbar {...props} />}>
          <FormTab label="Identificação">
            <TextInput source="name" label="Nome" InputProps={{ autoComplete: 'off' }} sx={{ minWidth: 300 }} />
            <TextInput source="email" label="Email" InputProps={{ autoComplete: 'off' }} validate={[email()]} sx={{ minWidth: 300 }} />
            <CellphoneInput />
            <DateInput
              source="birthDate"
              label="Data de Nascimento"
              format={dateInputFormatter}
              validate={validateFurtherDate()}
              sx={{ minWidth: 300 }}
            />
            <SelectInput
              source="gender"
              label="Sexo"
              InputProps={{ autoComplete: 'off' }}
              choices={[
                { id: 'M', name: 'Masculino' },
                { id: 'F', name: 'Feminino' },
                { id: 'X', name: 'Outro' },
              ]}
              sx={{ minWidth: 300 }}
            />
            <TextInput
              source="password"
              label="Alterar Senha"
              type="password"
              InputProps={{ autoComplete: 'new-password' }}
              validate={validatePassword}
              sx={{ minWidth: 300 }}
            />
            <SelectInput label='Grupo' source='groupId' choices={customerGroups} disabled={!permissions || !permissions.includes(PERMISSIONS.ADMIN)} helperText="Grupos foram substituídos por etiquetas — use o campo Etiquetas abaixo." sx={{ minWidth: 300 }} />
            <ReferenceArrayInput
              style={{ minWidth: 260 }}
              sort={{ field: 'name', order: 'ASC' }}
              filter={{ type: TagType.client }}
              perPage={null}
              source="tagIds"
              reference="chains/chainId/tag"
            >
              <SelectArrayInput
                label="Etiquetas"
                optionText="name" />
            </ReferenceArrayInput>
            <Permission permission={PERMISSIONS.POINT_RULES}>
              <CheckChainUsesModule module={ModuleKey.LIMITS}>
                <BooleanInput
                  source="ignoreLimits"
                  label="Pontuar sem limites"
                  helperText="Este cliente não passa pelos limites de pontuação da rede (quantidade de abastecimentos, pontos no período e intervalo entre abastecimentos)."
                  sx={{ marginBottom: 2 }}
                />
              </CheckChainUsesModule>
            </Permission>
            <Permission permission={PERMISSIONS.ADD_POINTS}>
              <Button
                variant="outlined"
                label="Adicionar Pontos"
                onClick={() => setDisplayBalanceModal(true)}
              />
            </Permission>
            <Permission permission={PERMISSIONS.CREATE_COUPON}>
              <Button
                sx={{ marginTop: 2 }}
                variant="outlined"
                label="Adicionar Cupom"
                onClick={() => setDisplayCouponModal(true)}
              />
            </Permission>
            <Permission permission={PERMISSIONS.UNIFIED_SEND}>
              <Button
                sx={{ marginTop: 2 }}
                variant="outlined"
                label="Enviar notificação"
                onClick={() => setDisplayNotificationModal(true)}
              />
            </Permission>
            {displayCouponModal && <Permission permission={PERMISSIONS.CREATE_COUPON}>
              <AddCoupon onClose={() => setDisplayCouponModal(false)} />
            </Permission>}
            {displayBalanceModal && <Permission permission={PERMISSIONS.ADD_POINTS}>
              <BalanceButton onClose={() => setDisplayBalanceModal(false)} />
            </Permission>
            }
            {displayNotificationModal && <Permission permission={PERMISSIONS.UNIFIED_SEND}>
              <CustomerUnifiedSendButton onClose={() => setDisplayNotificationModal(false)} />
            </Permission>}
          </FormTab>

          <FormTab label="Endereço">
            <TextInput source="street" label="Rua/Avenida/Logradouro" sx={{ minWidth: 400 }} InputProps={{ autoComplete: 'off' }} />
            <TextInput source="streetNumber" label="Número" InputProps={{ autoComplete: 'off' }} sx={{ minWidth: 300 }} />
            <TextInput source="complement" label="Complemento" InputProps={{ autoComplete: 'off' }} sx={{ minWidth: 300 }} />
            <TextInput source="neighbourhood" label="Bairro" InputProps={{ autoComplete: 'off' }} sx={{ minWidth: 300 }} />
            <TextInput source="city" label="Cidade" InputProps={{ autoComplete: 'off' }} sx={{ minWidth: 300 }} />
            <TextInput source="state" label="Estado" InputProps={{ autoComplete: 'off' }} sx={{ minWidth: 300 }} />
            <TextInput source="cep" label="CEP"
              InputProps={{ autoComplete: 'off' }}
              sx={{ minWidth: 300 }}
              format={(value) => {
                if (!value || value === "") {
                  return "";
                }
                const firstStep = value.substr(0, 5)
                const secondStep = value.substr(5, 3)
                let phoneString = '';

                if (firstStep) {
                  phoneString += `${firstStep}`;
                }
                if (secondStep) {
                  phoneString += `-${secondStep}`;
                }
                return phoneString;
              }}
              parse={(value) => {
                if (!value || value === '') {
                  return '';
                }
                return onlyNumbers(value).substring(0, 8);
              }}
            />
          </FormTab>
          <FormTab label="Observações">
            <ArrayInput source="customerNotes" label={false}>
              <SimpleFormIterator
                disableReordering
                disableClear
                disableRemove={(record) => !!(record && record.id)}
                addButton={<AddNoteButton />}
                sx={{ maxWidth: 720 }}
              >
                <FormDataConsumer>
                  {({ scopedFormData }) => scopedFormData?.id
                    ? <SavedNote note={scopedFormData} />
                    : <TextInput required source="note" label="Nova observação" multiline minRows={2} sx={{ width: '100%', maxWidth: 640 }} />}
                </FormDataConsumer>
              </SimpleFormIterator>
            </ArrayInput>
          </FormTab>
        </TabbedForm>
      </Edit>
    </Permission>
  )
}
